/* eslint-disable no-nested-ternary */

import type { InputsState, Labels, Recommendation } from './types'
import { getLabels } from './labels'

export function currency(omrPerMonth: number): string {
    if (!Number.isFinite(omrPerMonth)) return '—'
    return `${omrPerMonth.toFixed(3)} OMR/mo`
}

export function analyze(inputs: InputsState): {
    recs: Recommendation[]
    total: number
    labels: Labels
} {
    const L = getLabels(inputs.provider)
    const results: Recommendation[] = []
    let potential = 0

    const computePortion =
        inputs.workload === 'compute'
            ? 0.6
            : inputs.workload === 'general'
            ? 0.45
            : 0.3
    const storagePortion =
        inputs.workload === 'storage'
            ? 0.6
            : inputs.workload === 'general'
            ? 0.35
            : 0.25
    const transferPortion =
        inputs.workload === 'transfer'
            ? 0.5
            : inputs.workload === 'general'
            ? 0.2
            : 0.2

    // Rightsize compute
    if (inputs.ec2AvgUtil < 30 && inputs.ec2Instances > 0) {
        const computeSpend = inputs.monthlySpend * computePortion
        const save = computeSpend * 0.25
        potential += save
        results.push({
            id: 'compute-rightsize',
            title: `Rightsize low-utilization ${L.vmName}`,
            detail: `Average utilization is below 30%. Use ${L.computeOptimizer} to downsize instance families, reduce over-provisioned vCPU/RAM, and stop or hibernate idle instances. Automate off-hours shutdown where possible.`,
            estimatedMonthlySavings: save,
        })
    }

    // Commitment programs
    if (inputs.savingsPlanCoverage < 60 && inputs.ec2Instances > 0) {
        const computeSpend = inputs.monthlySpend * computePortion
        const save = computeSpend * 0.18
        potential += save
        results.push({
            id: 'savings-plans',
            title: `Increase ${L.savingsLabel} coverage`,
            detail:
                inputs.provider === 'aws' ||
                inputs.provider === 'gcp' ||
                inputs.provider === 'azure'
                    ? 'Coverage is below 60%. Evaluate 1–3 year commitments for steady compute usage. Start with partial coverage and expand after measuring baseline.'
                    : 'Where available, explore reserved capacity or enterprise agreements for steady usage. Otherwise, focus on sustained-use rightsizing and scheduling.',
            estimatedMonthlySavings: save,
        })
    }

    // Managed database rightsizing / engine choice
    if (inputs.rdsInstances > 0 && inputs.rdsAvgUtil < 35) {
        const dbSpend = inputs.monthlySpend * 0.15
        const save = dbSpend * 0.2
        potential += save
        results.push({
            id: 'rds-rightsize',
            title: `Rightsize ${L.dbName} or adopt serverless`,
            detail: `${L.dbName} utilization appears low. Downsize instances, enable storage autoscaling, and consider serverless options for spiky workloads. Apply instance stop/start for non-production.`,
            estimatedMonthlySavings: save,
        })
    }

    // Object storage lifecycle and storage class optimization
    if (inputs.s3StandardTb > inputs.s3IaTb) {
        const s3Spend = inputs.monthlySpend * storagePortion
        const save = s3Spend * 0.25
        potential += save
        results.push({
            id: 's3-lifecycle',
            title: `Apply ${L.objectStorage} lifecycle and storage classes`,
            detail: `Move infrequently accessed objects from hot to colder tiers (${L.objectStorageClasses}, if available). Enable lifecycle policies (or equivalent) for archival and deletion.`,
            estimatedMonthlySavings: save,
        })
    }

    // Block storage optimization and snapshot hygiene
    if (inputs.ebsTb > 0) {
        const ebsSpend = inputs.monthlySpend * 0.1
        const save = ebsSpend * 0.2
        potential += save
        results.push({
            id: 'ebs-gp3',
            title: `Optimize ${L.blockStorage} and prune snapshots`,
            detail: `For ${L.blockStorage}, ${L.blockStorageUpgrade}. Implement ${L.snapshotWord} lifecycle policies to expire old backups and remove orphaned copies.`,
            estimatedMonthlySavings: save,
        })
    }
    if (inputs.snapshotsTb > 0) {
        results.push({
            id: 'ebs-snapshots',
            title: 'Enforce snapshot retention',
            detail: 'Enable snapshot/backup lifecycle policies to retain only required recovery points. Audit and delete snapshots from terminated instances.',
        })
    }

    // Data transfer optimization
    if (inputs.xferOutGb > 1000) {
        const transferSpend = inputs.monthlySpend * transferPortion
        const save = transferSpend * 0.15
        potential += save
        results.push({
            id: 'data-transfer',
            title: 'Reduce data transfer out costs',
            detail: `Front with ${L.cdnName}, enable caching where available, and cache aggressively. Prefer ${L.privateNet} for internal traffic. Consider compression and smaller payloads.`,
            estimatedMonthlySavings: save,
        })
    }

    // Hygiene
    if (inputs.unusedEips > 0) {
        results.push({
            id: 'eip',
            title: `Release unused ${L.eipLabel}`,
            detail: 'Release unattached public IP addresses to avoid recurring charges. Tag and automate cleanup.',
        })
    }
    if (inputs.idleElbs > 0) {
        results.push({
            id: 'elb',
            title: 'Delete idle load balancers',
            detail: `Remove idle ${L.lbLabel} or consolidate listeners. Use autoscaling/target tracking to match demand.`,
        })
    }

    // General best practices
    results.push({
        id: 'graviton',
        title: L.armAndSpotTitle,
        detail: L.armAndSpotDetail,
    })

    return { recs: results, total: potential, labels: L }
}
